// client/src/pages/user/NewOrder.js
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../api/axiosInstance";

export default function NewOrder() {
  const [services, setServices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [link, setLink] = useState("");
  const [amount, setAmount] = useState(0);
  const [message, setMessage] = useState("");

  const [searchParams] = useSearchParams();

  /* ================= LOAD SERVICES ================= */
  useEffect(() => {
    api.get("/services").then((res) => {
      const list = res.data || [];
      setServices(list);
      setFiltered(list);

      const serviceId = searchParams.get("service");
      if (serviceId) {
        const found = list.find((s) => s._id === serviceId);
        if (found) {
          setSelected(found);
          setQuantity(found.min);
        }
      }
    });
  }, [searchParams]);

  /* ================= SEARCH ================= */
  const onSearch = (value) => {
    const q = value.toLowerCase();
    setFiltered(
      services.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          String(s.providerServiceId).includes(q)
      )
    );
  };

  /* ================= AMOUNT CALC ================= */
  useEffect(() => {
    if (!selected) return setAmount(0);

    const q = Number(quantity);
    if (!q || q <= 0) return setAmount(0);

    let total = 0;

    // PACKAGE SERVICE
    if (selected.min === 1 && selected.max === 1) {
      total = Number(selected.rate);
    }
    // BULK SERVICE
    else {
      total = (q * Number(selected.rate)) / 1000;
    }

    setAmount(Number(total.toFixed(2)));
  }, [selected, quantity]);

  /* ================= PLACE ORDER ================= */
  const placeOrder = async () => {
    setMessage("");

    if (!selected) return setMessage("Select a service");
    if (!link) return setMessage("Enter link");

    const q = Number(quantity);
    if (q < selected.min || q > selected.max) {
      return setMessage(
        `Quantity must be between ${selected.min} and ${selected.max}`
      );
    }

    try {
      await api.post("/orders", {
        serviceId: selected._id,
        link,
        quantity: q,
      });

      // 🔥 Fetch updated dashboard (fresh wallet)
      const dash = await api.get("/user/dashboard");

      setMessage("✅ Order placed successfully");

      // 🔥 Instantly update wallet in context
      window.dispatchEvent(
        new CustomEvent("wallet_updated", {
          detail: { balance: dash.data.wallet },
        })
      );

      // Reset form
      setSelected(null);
      setQuantity("");
      setLink("");
      setAmount(0);

    } catch (err) {
      setMessage(err.response?.data?.message || "Order failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto grid lg:grid-cols-[2fr,1fr] gap-6">
      {/* SERVICES */}
      <div className="card-glass p-6">
        <h2 className="text-xl font-semibold mb-4">Select Service</h2>

        <input
          className="input-glass mb-3"
          placeholder="Search service name or ID"
          onChange={(e) => onSearch(e.target.value)}
        />

        <div className="max-h-[520px] overflow-y-auto border border-white/10 rounded-xl">
          {filtered.map((s) => (
            <div
              key={s._id}
              onClick={() => {
                setSelected(s);
                setQuantity(s.min);
              }}
              className={`p-3 border-b border-white/10 cursor-pointer ${
                selected?._id === s._id ? "bg-purple-900/40" : ""
              }`}
            >
              <div className="font-medium">
                {s.providerServiceId} | {s.name}
              </div>

              <div className="text-xs text-gray-300 flex justify-between">
                <span>
                  {s.platform} • {s.category}
                </span>
                <span>₹{s.rate} / 1000</span>
              </div>

              <div className="text-xs text-gray-400">
                Min {s.min} • Max {s.max}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ORDER PANEL */}
      <div className="card-glass p-6 space-y-4">
        <h2 className="text-lg font-semibold">Order Details</h2>

        <p>
          Service: <b>{selected?.name || "None selected"}</b>
        </p>

        <input
          className="input-glass"
          placeholder="Link"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />

        <input
          className="input-glass"
          type="number"
          value={quantity}
          min={selected?.min}
          max={selected?.max}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder={
            selected
              ? `Min ${selected.min} | Max ${selected.max}`
              : "Quantity"
          }
        />

        {selected && (
          <div className="text-sm text-gray-400">
            Min {selected.min} • Max {selected.max}
          </div>
        )}

        <div className="text-xl font-bold text-purple-300">
          ₹ {amount.toFixed(2)}
        </div>

        <button
          onClick={placeOrder}
          disabled={!selected || amount <= 0}
          className="btn-primary w-full disabled:opacity-50"
        >
          Place Order
        </button>

        {message && (
          <p className="text-sm text-purple-300">{message}</p>
        )}
      </div>
    </div>
  );
}
